// src/components/DetalleReserva.jsx
import React, { useState, useEffect } from "react";
import { Check, Info } from "lucide-react";
import { supabase } from "../../Supabase/SupabaseClient";
import "./DetalleReserva.css";
import Footer from "../../Components/Footer/Footer";

const DetalleReserva = ({ reservaId, onNavigateBack }) => {
  const [reserva, setReserva] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelando, setCancelando] = useState(null); // Guardamos el ID de la reserva que se está cancelando
  const [cancelacionExitosa, setCancelacionExitosa] = useState(false);

  useEffect(() => {
    const fetchReservaDetalle = async () => {
      try {
        // Verificar si hay un usuario autenticado
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError || !user) {
          throw new Error("Debes iniciar sesión para ver tus reservas");
        }

        // Construir la consulta base
        let query = supabase
          .from("reservas")
          .select(`
            id,
            dia,
            horario,
            created_at,
            servicios (
              id,
              nombre,
              descripcion,
              lugar
            ),
            usuarios (
              id,
              nombre,
              email
            )
          `);

        // Si se proporciona un ID específico, filtrar por ese ID
        if (reservaId) {
          query = query.eq("id", reservaId).eq("usuario_id", user.id);
        } else {
          // Si no hay ID específico, mostrar las reservas del usuario actual
          query = query.eq("usuario_id", user.id);
        }

        // Ordenar por fecha más reciente primero
        query = query.order("dia", { ascending: false });

        const { data, error: fetchError } = await query;

        if (fetchError) throw fetchError;

        if (reservaId) {
          // Si se buscaba una reserva específica
          const reservaEncontrada = data && data.length > 0 ? data[0] : null;
          setReserva(reservaEncontrada);
          if (!reservaEncontrada) {
            setError("No se encontró la reserva solicitada o no tienes permisos para verla");
          }
        } else {
          // Si se buscaban todas las reservas del usuario
          setReserva(data || []);
        }
      } catch (err) {
        console.error("Error al cargar los detalles de la reserva:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchReservaDetalle();
  }, [reservaId]);

  // Función para cancelar una reserva
  const cancelarReserva = async (id) => {
    if (!window.confirm("¿Estás seguro que deseas cancelar esta reserva?")) {
      return;
    }
    
    setCancelando(id);
    try {
      const { error } = await supabase
        .from("reservas")
        .delete()
        .eq("id", id);
      
      if (error) throw error;
      
      // Si la cancelación fue exitosa
      setCancelacionExitosa(true);
      
      // Actualizar la lista de reservas eliminando la cancelada
      if (Array.isArray(reserva)) {
        setReserva(reserva.filter(r => r.id !== id));
      } else {
        // Si estamos viendo una sola reserva, mostrar mensaje de éxito solamente
        setReserva(null);
      }
      
      // Ocultar el mensaje de éxito después de 3 segundos
      setTimeout(() => {
        setCancelacionExitosa(false);
      }, 3000);
      
    } catch (err) {
      console.error("Error al cancelar la reserva:", err);
      setError("No se pudo cancelar la reserva: " + err.message);
    } finally {
      setCancelando(null);
    }
  };

  // Función para formatear la fecha
  const formatearFecha = (fechaStr) => {
    const fecha = new Date(fechaStr + 'T00:00:00');
    return fecha.toLocaleDateString('es-ES', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Función para verificar si una reserva está en el pasado
  const esReservaPasada = (fechaStr, horario) => {
    const fechaReserva = new Date(fechaStr + 'T' + horario + ':00');
    const ahora = new Date();
    return fechaReserva < ahora;
  };

  if (loading) {
    return (
      <div className="dr-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Cargando detalles de la reserva...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dr-page">
        <div className="error-container">
          <div className="error-message">
            <h3>Error</h3>
            <p>{error}</p>
            {onNavigateBack && (
              <button onClick={onNavigateBack} className="btn btn-secondary">
                Volver
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (cancelacionExitosa) {
    return (
      <div className="dr-page">
        <div className="success-container">
          <div className="success-message">
            <div className="success-icon" aria-hidden="true"><Check size={32} strokeWidth={3} /></div>
            <h3>¡Reserva cancelada exitosamente!</h3>
            <p>Tu reserva ha sido cancelada correctamente.</p>
            {onNavigateBack ? (
              <button onClick={onNavigateBack} className="btn btn-primary">
                Volver a mis reservas
              </button>
            ) : (
              <a href="/detalle-reserva" className="btn btn-primary">
                Volver a mis reservas
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!reserva || (Array.isArray(reserva) && reserva.length === 0)) {
    return (
      <div className="dr-page">
        <div className="empty-container">
          <div className="empty-message">
            <h3>No hay reservas</h3>
            <p>No se encontraron reservas registradas.</p>
            {onNavigateBack && (
              <button onClick={onNavigateBack} className="btn btn-secondary">
                Volver
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Si es una sola reserva
  if (!Array.isArray(reserva)) {
    const reservaPasada = esReservaPasada(reserva.dia, reserva.horario);
    
    return (
      <div className="dr-page">
        <div className="detalle-reserva-container">
          <div className="detalle-reserva-card">
            <div className="card-header">
              <h2 id="titulo">Detalle de tu Reserva</h2>
              {reservaPasada && (
                <span className="badge badge-past">Finalizada</span>
              )}
            </div>
            
            <div className="card-content">
              <div className="info-grid">
                <div className="info-item">
                  <label>Servicio:</label>
                  <span>{reserva.servicios?.nombre || "No especificado"}</span>
                </div>
                
                <div className="info-item">
                  <label>Fecha:</label>
                  <span>{formatearFecha(reserva.dia)}</span>
                </div>
                
                <div className="info-item">
                  <label>Hora:</label>
                  <span>{reserva.horario} hrs</span>
                </div>
                
                <div className="info-item">
                  <label>Lugar:</label>
                  <span>{reserva.servicios?.lugar || "No especificado"}</span>
                </div>
                
                {reserva.servicios?.descripcion && (
                  <div className="info-item full-width">
                    <label>Descripción:</label>
                    <span>{reserva.servicios.descripcion}</span>
                  </div>
                )}
                
                <div className="info-item">
                  <label>Reservado el:</label>
                  <span>{new Date(reserva.created_at).toLocaleDateString('es-ES')}</span>
                </div>
              </div>
            </div>
            
            <div className="card-footer">
              <div className="important-notice">
                <div className="notice-icon" aria-hidden="true"><Info size={18} /></div>
                <p>
                  <strong>Importante:</strong> Si necesitas cancelar o reprogramar tu reserva, 
                  por favor contáctanos con al menos 24 horas de anticipación.
                </p>
              </div>
              
              <div className="actions">
                {onNavigateBack && (
                  <button onClick={onNavigateBack} className="btn btn-secondary">
                    Volver
                  </button>
                )}
                
                {!reservaPasada && (
                  <button 
                    onClick={() => cancelarReserva(reserva.id)}
                    disabled={cancelando === reserva.id}
                    className="btn btn-danger"
                  >
                    {cancelando === reserva.id ? (
                      <>
                        <span className="spinner"></span>
                        Cancelando...
                      </>
                    ) : (
                      "Cancelar reserva"
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Si son múltiples reservas
  return (
    <>
    <div className="dr-page">
      <div className="lista-reservas-container">
        <div className="lista-reservas-header">
          <h2 id="titulo">Tus Reservas</h2>
          <span className="reservas-count">{reserva.length} reserva{reserva.length !== 1 ? 's' : ''}</span>
        </div>
        
        <div className="reservas-grid">
          {reserva.map((r) => {
            const reservaPasada = esReservaPasada(r.dia, r.horario);
            
            return (
              <div key={r.id} className={`reserva-card ${reservaPasada ? 'past' : ''}`}>
                <div className="reserva-card-header">
                  <h3>{r.servicios?.nombre || "Servicio no especificado"}</h3>
                  {reservaPasada && (
                    <span className="badge badge-past">Finalizada</span>
                  )}
                </div>
                
                <div className="reserva-card-content">
                  <div className="reserva-info">
                    <div className="info-row">
                      <div className="info-column">
                        <label>Fecha y Hora:</label>
                        <span>{formatearFecha(r.dia)} a las {r.horario} hrs</span>
                      </div>
                      
                      <div className="info-column">
                        <label>Lugar:</label>
                        <span>{r.servicios?.lugar || "No especificado"}</span>
                      </div>
                    </div>
                    
                    {r.servicios?.descripcion && (
                      <div className="descripcion">
                        <label>Descripción:</label>
                        <span>{r.servicios.descripcion}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="reserva-card-footer">
                  {!reservaPasada && (
                    <button 
                      onClick={() => cancelarReserva(r.id)}
                      disabled={cancelando === r.id}
                      className="btn btn-danger btn-sm"
                    >
                      {cancelando === r.id ? (
                        <>
                          <span className="spinner"></span>
                          Cancelando...
                        </>
                      ) : (
                        "Cancelar reserva"
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
      <Footer />
    </>
  );
};

export default DetalleReserva;